import styles from "./styles.scss";
import { FC, useEffect, useRef, useState } from "react";
import {
  offset,
  useFloating,
  flip,
  arrow,
  FloatingPortal,
  shift,
} from "@floating-ui/react";
import clsx from "clsx";
import { useClickOutside } from "@/hooks";
import { PopupMenuProps } from "./types";
import { DEFAULT_PLACEMENT } from "./constants";
import { getArrowPlacementSide } from "./utils";

export const PopupMenu: FC<PopupMenuProps> = ({
  className,
  containerClassName,
  placement = DEFAULT_PLACEMENT,
  mainAxisOffset = 0,
  crossAxisOffset = 0,
  hasBottomArrow,
  arrowClassName,
  renderTrigger,
  renderContent,
  onOpenStateChange,
  strategy = "absolute",
  isOpenInitial = false,
}) => {
  const containerRef = useRef(null);
  const arrowRef = useRef(null);
  const [isOpen, setIsOpen] = useState(isOpenInitial);

  const isFixedStrategy = strategy === "fixed";

  const {
    x,
    y,
    placement: finalPlacement,
    strategy: finalStrategy,
    update,
    middlewareData: { arrow: { x: arrowX, y: arrowY } = {} },
    refs: { floating: floatingRef, setFloating, setReference },
  } = useFloating({
    strategy,
    placement,
    middleware: [
      offset({ mainAxis: mainAxisOffset, crossAxis: crossAxisOffset }),
      shift({ padding: 4 }),
      flip(),
      arrow({ element: arrowRef, padding: 12 }),
    ],
  });

  const arrowPlacementSide = getArrowPlacementSide(finalPlacement);

  useEffect(() => {
    if (isOpen) {
      update();
    }
  }, [isOpen, update]);

  const handleTriggerClick = () => {
    setIsOpen(prevState => !prevState);

    if (onOpenStateChange) {
      onOpenStateChange(!isOpen);
    }
  };

  const handlePopupMenuClose = () => {
    setIsOpen(false);

    if (onOpenStateChange) {
      onOpenStateChange(false);
    }
  };

  useClickOutside(
    isFixedStrategy ? [floatingRef, containerRef] : containerRef,
    handlePopupMenuClose,
  );

  const renderMenu = () =>
    isOpen && (
      <div
        ref={setFloating}
        style={{
          position: finalStrategy,
          top: y ?? "",
          left: x ?? "",
        }}
        className={clsx(styles.menu, className)}
      >
        {renderContent({ onClose: handlePopupMenuClose })}

        {hasBottomArrow && (
          <div
            className={clsx(
              arrowClassName,
              styles.arrow,
              styles[arrowPlacementSide as never],
            )}
            ref={arrowRef}
            style={{
              top: arrowY ?? "",
              left: arrowX ?? "",
              [arrowPlacementSide]: -8,
            }}
          />
        )}
      </div>
    );

  return (
    <div className={containerClassName} ref={containerRef}>
      {renderTrigger({
        reference: setReference,
        onClick: handleTriggerClick,
        isOpen,
      })}

      {isFixedStrategy ? (
        <FloatingPortal>{renderMenu()}</FloatingPortal>
      ) : (
        renderMenu()
      )}
    </div>
  );
};
